import React, { useState, useEffect } from "react";
import api from "../api";
import { trackEvent } from "../utils/tracking";
import "../styles/cookie-banner.css";

const COOKIE_CONSENT_KEY = "takofy_cookie_consent";
const CLIENT_ID_KEY = "takofy_client_id";

function generateClientId() {
  return "client_" + Math.random().toString(36).substr(2, 9) + Date.now();
}

function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  
  const [preferences, setPreferences] = useState({
    essential: true, // always true
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // 1. Check local storage first
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (storedConsent) {
      try {
        const parsed = JSON.parse(storedConsent);
        setPreferences(parsed);
      } catch (e) {
        // invalid JSON
      }
      return; // Already consented
    }

    // 2. Either no consent locally or we need to sync from backend
    let clientId = localStorage.getItem(CLIENT_ID_KEY);
    if (!clientId) {
      clientId = generateClientId();
      localStorage.setItem(CLIENT_ID_KEY, clientId);
      setShowBanner(true);
    } else {
      // Fetch from backend
      api.get(`/consent/${clientId}`)
        .then(res => {
          if (res.data && res.data.preferences) {
             setPreferences(res.data.preferences);
             localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(res.data.preferences));
          } else {
             setShowBanner(true);
          }
        })
        .catch(err => {
          setShowBanner(true);
        });
    }
  }, []);

  const handleSaveConsent = async (newPreferences) => {
    try {
      setPreferences(newPreferences);
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newPreferences));
      setShowBanner(false);
      setShowPreferences(false);
      
      const clientId = localStorage.getItem(CLIENT_ID_KEY);
      if (clientId) {
        await api.post("/consent", { clientId, preferences: newPreferences });
      }
      
      trackEvent("update_cookie_consent", newPreferences);
    } catch (e) {
      console.error("Failed to save consent to backend", e);
    }
  };

  const handleAcceptAll = () => {
    handleSaveConsent({ essential: true, analytics: true, marketing: true });
  };

  const handleRejectAll = () => {
    handleSaveConsent({ essential: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    handleSaveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-banner-overlay">
      <div className={`cookie-banner ${showPreferences ? 'expanded' : ''}`}>
        {!showPreferences ? (
          <div className="cookie-banner-simple">
            <div className="cookie-banner-content">
              <h4>Çerez Tercihleri</h4>
              <p>
                Size daha iyi bir deneyim sunmak için web sitemizde çerezler kullanıyoruz. 
                Gerekli çerezler sitenin çalışması için kullanılırken, diğer çerezler daha kişiselleştirilmiş bir deneyim sağlamamıza yardımcı olur.
              </p>
            </div>
            <div className="cookie-banner-actions">
              <button className="btn-cookie btn-cookie-text" onClick={() => setShowPreferences(true)}>
                Tercihleri Yönet
              </button>
              <button className="btn-cookie btn-cookie-outline" onClick={handleRejectAll}>
                Reddet
              </button>
              <button className="btn-cookie btn-cookie-primary" onClick={handleAcceptAll}>
                Tümünü Kabul Et
              </button>
            </div>
          </div>
        ) : (
          <div className="cookie-banner-advanced">
            <h4>Etkileşim ve Reklam Çerezleri Tercihleri</h4>
            <div className="cookie-pref-item">
              <div className="cookie-pref-info">
                <h5>Zorunlu Çerezler</h5>
                <p>Web sitesinin düzgün çalışması için gereklidir ve sistemlerimizde kapatılamaz.</p>
              </div>
              <div className="cookie-pref-toggle">
                <input type="checkbox" checked disabled id="cb-essential" />
                <label htmlFor="cb-essential">Açık</label>
              </div>
            </div>

            <div className="cookie-pref-item">
              <div className="cookie-pref-info">
                <h5>Analitik Çerezler</h5>
                <p>Site trafiğini ve davranışları analiz etmemize yardımcı olarak site performansını artırmamızı sağlar.</p>
              </div>
              <div className="cookie-pref-toggle">
                <input 
                  type="checkbox" 
                  id="cb-analytics" 
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})} 
                />
                <label htmlFor="cb-analytics">Açık</label>
              </div>
            </div>

            <div className="cookie-pref-item">
              <div className="cookie-pref-info">
                <h5>Pazarlama Çerezleri</h5>
                <p>İlgi alanlarınıza göre size daha uygun reklamlar göstermek için kullanılır.</p>
              </div>
              <div className="cookie-pref-toggle">
                <input 
                  type="checkbox" 
                  id="cb-marketing" 
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})} 
                />
                <label htmlFor="cb-marketing">Açık</label>
              </div>
            </div>

            <div className="cookie-banner-actions advanced-actions">
              <button className="btn-cookie btn-cookie-outline" onClick={() => setShowPreferences(false)}>
                Geri Git
              </button>
              <button className="btn-cookie btn-cookie-primary" onClick={handleSavePreferences}>
                Seçimlerimi Kaydet
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookieBanner;
